import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendWebhookData } from '../../services/webhook';
import Button from '../../components/Button';
import { Check, MessageSquare, ShoppingCart, Store, Bot, Phone, Headphones, ArrowRight, SendIcon, CheckIcon, SettingsIcon, LayoutDashboard, BarChart, ShoppingBag, Users, ChevronRight, SearchIcon, UserIcon, BriefcaseIcon } from 'lucide-react';
import Wizard from '../../components/Wizard';
export default function HebrewLandingPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    website: '',
    token: '',
    phone: ''
  });
  const navigate = useNavigate();
  const formRef = useRef(null);
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Send data to webhook using the same function as Wizard
      const success = await sendWebhookData({
        email: formData.email,
        catalogUrl: formData.website,
        agentGoal: formData.token,
        phone: formData.phone,
        formType: 'trial_signup'
      });
      if (success) {
        // Redirect to thank you page, same as Wizard
        navigate('/thank-you');
      } else {
        alert('היתה שגיאה בשליחת הטופס. אנא נסו שנית.');
      }
    } catch (error) {
      console.error('Error during form submission:', error);
      alert('היתה שגיאה בשליחת הטופס. אנא נסו שנית.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-white text-slate-800" dir="rtl" data-id="element-3156">
      {/* HERO */}
      <section className="container mx-auto px-4 pt-16 pb-12 max-w-6xl text-right" data-id="element-3157">
        <div className="mb-3 flex flex-wrap gap-2 justify-end" data-id="element-3158">
          <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium" data-id="element-3159">
            AI Agents
          </span>
          <span className="px-3 py-1 bg-blue-500/10 text-blue-500 rounded-full text-sm font-medium" data-id="element-3160">
            eCommerce
          </span>
          <span className="px-3 py-1 bg-green-500/10 text-green-500 rounded-full text-sm font-medium" data-id="element-3161">
            Multi‑Channel
          </span>
        </div>
        <h1 className="text-4xl md:text-6xl font-extrabold leading-tight bg-clip-text text-transparent bg-gradient-to-l from-primary to-blue-500" data-id="element-3162">
          סוכן מכירות מבוסס בינה מלאכותית ללקוחות קונימבו
        </h1>
        <p className="mt-5 text-slate-600 md:text-xl max-w-3xl mr-0 ml-auto" data-id="element-3163">
          מטמיעים{' '}
          <strong className="text-primary" data-id="element-3164">סוכני מכירות חכמים (AI)</strong>{' '}
          שמבינים את הקטלוג שלכם ומוכרים ללקוחות ב‑
          <strong className="text-blue-500" data-id="element-3165">אתר</strong>, ב‑
          <strong className="text-green-500" data-id="element-3166">סושיאל</strong>, בתוך{' '}
          <strong className="text-amber-500" data-id="element-3167">ChatGPT / GPT Store</strong>, ב‑
          <strong className="text-red-500" data-id="element-3168">WhatsApp</strong> וגם דרך{' '}
          <strong className="text-teal-500" data-id="element-3169">מערכת שירות הלקוחות</strong> הקיימת.
        </p>
        <div className="mt-8 flex flex-wrap gap-4 justify-end" data-id="element-3170">
          <a href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ3d8hIh-SBmquhBLzT0K2NNUsyp9W7OJz3kZC5dS4y2t7kSLPqcz7Ll5kRBTrX_hUBi2XAKTHHq" target="_blank" rel="noreferrer" className="px-6 py-3 bg-primary text-white rounded-xl font-bold shadow-lg hover:bg-primary/90 transition-all" data-id="element-3171">
            קבעו פגישת היכרות
          </a>
          <button onClick={scrollToForm} className="px-6 py-3 bg-gray-100 border border-gray-200 rounded-xl text-slate-700 font-bold hover:bg-gray-200 transition-all" data-id="element-3172">
            צפו ב демо
          </button>
        </div>
        <p className="mt-3 text-sm text-primary font-medium" data-id="element-3173">
          חודש ראשון חינם — למבקשים עד 15 בספטמבר
        </p>
      </section>

      {/* AI DEMO VISUALIZATION */}
      <section className="container mx-auto px-4 py-12 max-w-6xl" data-id="element-3174">
        <div className="relative bg-gray-50 rounded-2xl p-8 border border-gray-200 shadow-xl mb-16 overflow-hidden" data-id="element-3175">
          {/* YouTube video background */}
          <div className="absolute inset-0 w-full h-full" data-id="element-3176">
            <iframe src="https://www.youtube.com/embed/CtTtQDBqYmw?autoplay=1&mute=1&loop=1&controls=0&disablekb=1&playlist=CtTtQDBqYmw" title="Shopilots Demo Video" className="w-full h-full object-cover" allow="autoplay; encrypted-media" frameBorder="0" allowFullScreen data-id="element-3177"></iframe>
            {/* Overlay to make buttons more visible */}
            <div className="absolute inset-0 bg-black/30" data-id="element-3178"></div>
          </div>
          {/* Buttons floating on top of video */}
          <div className="relative z-10 flex justify-center py-40" data-id="element-3179">
            <div className="flex flex-wrap gap-4" data-id="element-3180">
              <button onClick={scrollToForm} className="px-6 py-3 bg-primary text-white rounded-xl font-bold shadow-lg hover:bg-primary/90 transition-all" data-id="element-3181">
                התחילו עם סוכן AI משלכם
              </button>
              <a href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ3d8hIh-SBmquhBLzT0K2NNUsyp9W7OJz3kZC5dS4y2t7kSLPqcz7Ll5kRBTrX_hUBi2XAKTHHq" target="_blank" rel="noreferrer" className="px-6 py-3 bg-white/90 border border-gray-200 rounded-xl text-slate-700 font-bold hover:bg-white transition-all" data-id="element-3182">
                קבעו פגישת היכרות
              </a>
            </div>
          </div>
        </div>
        <div className="text-center mb-8" data-id="element-3183">
          <button onClick={scrollToForm} className="px-6 py-3 bg-primary text-white rounded-xl font-bold shadow-lg hover:bg-primary/90 transition-all" data-id="element-3184">
            התחילו עם סוכן AI משלכם
          </button>
        </div>
      </section>

      {/* CHANNELS */}
      <section className="container mx-auto px-4 py-12 max-w-6xl bg-gray-50" data-id="element-3185">
        <h2 className="text-3xl font-bold text-right mb-8 text-slate-800" data-id="element-3186">
          ערוצי מכירה <span className="text-primary" data-id="element-3187">במקום אחד</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3188">
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-md" data-id="element-3189">
            <Store className="h-12 w-12 text-blue-500 mb-4" data-id="element-3190" />
            <h3 className="text-xl font-bold mb-3 text-right" data-id="element-3191">אתר החנות</h3>
            <p className="text-slate-600 text-right" data-id="element-3192">
              הטמעה ישירה באתר (Konimbo / Shopify / Custom). הלקוח משוחח עם
              הסוכן, מקבל המלצות מהקטלוג ומוסיף לסל.
            </p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-md" data-id="element-3193">
            <MessageSquare className="h-12 w-12 text-green-500 mb-4" data-id="element-3194" />
            <h3 className="text-xl font-bold mb-3 text-right" data-id="element-3195">סושיאל מדיה</h3>
            <p className="text-slate-600 text-right" data-id="element-3196">
              פייסבוק, אינסטגרם וטיקטוק – תגובות בהודעות ובפוסטים. מכירה מהפיד
              בלי לצאת מהאפליקציה.
            </p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-md" data-id="element-3197">
            <Bot className="h-12 w-12 text-primary mb-4" data-id="element-3198" />
            <h3 className="text-xl font-bold mb-3 text-right" data-id="element-3199">
              ChatGPT / GPT Store
            </h3>
            <p className="text-slate-600 text-right" data-id="element-3200">
              נוכחות של המותג ב‑ChatGPT. הסוכן מסביר, משווה ומוביל לרכישה – רק
              מתוך הקטלוג שלכם.
            </p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-md" data-id="element-3201">
            <Phone className="h-12 w-12 text-red-500 mb-4" data-id="element-3202" />
            <h3 className="text-xl font-bold mb-3 text-right" data-id="element-3203">
              WhatsApp Business
            </h3>
            <p className="text-slate-600 text-right" data-id="element-3204">
              הלקוחות שולחים הודעה ומקבלים תשובה עם קישורי רכישה, מעקב הזמנה
              ושירות – אוטומטי.
            </p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-md" data-id="element-3205">
            <Headphones className="h-12 w-12 text-amber-500 mb-4" data-id="element-3206" />
            <h3 className="text-xl font-bold mb-3 text-right" data-id="element-3207">
              שירות לקוחות קיים
            </h3>
            <p className="text-slate-600 text-right" data-id="element-3208">
              חיבור ל‑CRM/Helpdesk. מענה אוטומטי לשאלות נפוצות, פתיחת קריאה
              והעברה לנציג אנושי בעת הצורך.
            </p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-md" data-id="element-3209">
            <ShoppingCart className="h-12 w-12 text-teal-500 mb-4" data-id="element-3210" />
            <h3 className="text-xl font-bold mb-3 text-right" data-id="element-3211">
              מוכר מהקטלוג בלבד
            </h3>
            <p className="text-slate-600 text-right" data-id="element-3212">
              התאמה לדגשים של אלקטרוניקה וחשמל ביתי: נוחות שימוש, הסברים פשוטים,
              והמלצות עם שקיפות מלאה.
            </p>
          </div>
        </div>
        <div className="mt-8 text-center" data-id="element-3213">
          <button onClick={scrollToForm} className="px-6 py-3 bg-primary text-white rounded-xl font-bold shadow-lg hover:bg-primary/90 transition-all" data-id="element-3214">
            הרשמו לתקופת ניסיון ללא התחייבות
          </button>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="container mx-auto px-4 py-12 max-w-6xl text-right" data-id="element-3215">
        <h2 className="text-3xl font-bold mb-8 text-slate-800" data-id="element-3216">
          איך זה <span className="text-primary" data-id="element-3217">עובד</span>
        </h2>
        <div className="relative" data-id="element-3218">
          {/* Connector line */}
          <div className="absolute right-10 top-0 bottom-0 w-1 bg-primary/20 hidden md:block" data-id="element-3219"></div>
          <div className="space-y-12" data-id="element-3220">
            <div className="relative md:mr-16" data-id="element-3221">
              <div className="absolute right-[-40px] top-0 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center text-2xl font-bold text-primary hidden md:flex" data-id="element-3222">
                1
              </div>
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-md" data-id="element-3223">
                <h3 className="text-xl font-bold mb-3" data-id="element-3224">חיבור</h3>
                <p className="text-slate-600" data-id="element-3225">
                  מתחברים לקטלוג (API/פיד). לקונימבו – אפשר עם טוקן קריאה בלבד.
                  הסוכן לומד את כל המוצרים, המחירים, והמלאי שלכם.
                </p>
              </div>
            </div>
            <div className="relative md:mr-16" data-id="element-3226">
              <div className="absolute right-[-40px] top-0 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center text-2xl font-bold text-primary hidden md:flex" data-id="element-3227">
                2
              </div>
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-md" data-id="element-3228">
                <h3 className="text-xl font-bold mb-3" data-id="element-3229">למידה</h3>
                <p className="text-slate-600" data-id="element-3230">
                  מיפוי מאפיינים חשובים, ניהול מילון מושגים, והכשרת הסוכן למותג
                  שלכם. התאמה לקהל היעד ולשפת המותג.
                </p>
              </div>
            </div>
            <div className="relative md:mr-16" data-id="element-3231">
              <div className="absolute right-[-40px] top-0 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center text-2xl font-bold text-primary hidden md:flex" data-id="element-3232">
                3
              </div>
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-md" data-id="element-3233">
                <h3 className="text-xl font-bold mb-3" data-id="element-3234">אקטיבציה</h3>
                <p className="text-slate-600" data-id="element-3235">
                  הטמעה בערוצים שתרצו: אתר, סושיאל, ChatGPT, וואטסאפ ושירות
                  לקוחות. התאמה לכל פלטפורמה וחווית משתמש.
                </p>
              </div>
            </div>
            <div className="relative md:mr-16" data-id="element-3236">
              <div className="absolute right-[-40px] top-0 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center text-2xl font-bold text-primary hidden md:flex" data-id="element-3237">
                4
              </div>
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-md" data-id="element-3238">
                <h3 className="text-xl font-bold mb-3" data-id="element-3239">צמיחה</h3>
                <p className="text-slate-600" data-id="element-3240">
                  דוחות על כוונות, שיחות והמרות – כדי לשפר מלאי, תוכן ו‑UX.
                  שיפור מתמיד של הסוכן והגדלת ההמרות.
                </p>
                <div className="grid grid-cols-3 gap-4 mt-6" data-id="element-3241">
                  <div className="bg-gray-50 rounded-lg p-3 text-center" data-id="element-3242">
                    <LayoutDashboard className="h-6 w-6 text-primary mx-auto mb-2" data-id="element-3243" />
                    <p className="text-sm font-bold" data-id="element-3244">דשבורד אנליטיקס</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center" data-id="element-3245">
                    <BarChart className="h-6 w-6 text-blue-500 mx-auto mb-2" data-id="element-3246" />
                    <p className="text-sm font-bold" data-id="element-3247">דוחות המרה</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center" data-id="element-3248">
                    <Users className="h-6 w-6 text-green-500 mx-auto mb-2" data-id="element-3249" />
                    <p className="text-sm font-bold" data-id="element-3250">לידים איכותיים</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-10 text-center" data-id="element-3251">
          <button onClick={scrollToForm} className="px-6 py-3 bg-primary text-white rounded-xl font-bold shadow-lg hover:bg-primary/90 transition-all" data-id="element-3252">
            רוצים להתחיל? לחצו כאן
          </button>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="container mx-auto px-4 py-12 max-w-6xl text-right bg-gray-50" data-id="element-3253">
        <div className="bg-white rounded-xl p-8 border border-gray-200 shadow-lg" data-id="element-3254">
          <h2 className="text-3xl font-bold mb-6 text-slate-800" data-id="element-3255">
            מודלי <span className="text-primary" data-id="element-3256">תמחור</span> גמישים
          </h2>
          <div className="grid md:grid-cols-3 gap-6" data-id="element-3257">
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200" data-id="element-3258">
              <div className="text-primary text-xl font-bold mb-3" data-id="element-3259">
                מבוסס ביצועים
              </div>
              <p className="text-slate-600 mb-4" data-id="element-3260">
                תשלום לפי תוצאות - המרות, רכישות, או לידים שהסוכן מייצר.
              </p>
              <div className="flex items-center gap-2 text-green-500 mb-2" data-id="element-3261">
                <Check className="h-5 w-5" data-id="element-3262" />
                <span data-id="element-3263">אפס סיכון</span>
              </div>
              <div className="flex items-center gap-2 text-green-500" data-id="element-3264">
                <Check className="h-5 w-5" data-id="element-3265" />
                <span data-id="element-3266">תשלום רק על תוצאות</span>
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200" data-id="element-3267">
              <div className="text-blue-500 text-xl font-bold mb-3" data-id="element-3268">
                לפי שימוש
              </div>
              <p className="text-slate-600 mb-4" data-id="element-3269">
                תשלום לפי כמות השיחות או האינטראקציות עם הסוכן.
              </p>
              <div className="flex items-center gap-2 text-green-500 mb-2" data-id="element-3270">
                <Check className="h-5 w-5" data-id="element-3271" />
                <span data-id="element-3272">גמישות מלאה</span>
              </div>
              <div className="flex items-center gap-2 text-green-500" data-id="element-3273">
                <Check className="h-5 w-5" data-id="element-3274" />
                <span data-id="element-3275">ללא התחייבות</span>
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200" data-id="element-3276">
              <div className="text-teal-500 text-xl font-bold mb-3" data-id="element-3277">
                מנוי חודשי
              </div>
              <p className="text-slate-600 mb-4" data-id="element-3278">
                תשלום קבוע חודשי עם שימוש בלתי מוגבל בכל הערוצים.
              </p>
              <div className="flex items-center gap-2 text-green-500 mb-2" data-id="element-3279">
                <Check className="h-5 w-5" data-id="element-3280" />
                <span data-id="element-3281">צמיחה ללא הגבלה</span>
              </div>
              <div className="flex items-center gap-2 text-green-500" data-id="element-3282">
                <Check className="h-5 w-5" data-id="element-3283" />
                <span data-id="element-3284">תקציב קבוע וידוע מראש</span>
              </div>
            </div>
          </div>
          <div className="mt-8 bg-primary/10 rounded-xl p-6 border border-primary/20" data-id="element-3285">
            <div className="flex items-start gap-3" data-id="element-3286">
              <div className="bg-primary/10 p-2 rounded-lg" data-id="element-3287">
                <CheckIcon className="h-6 w-6 text-primary" data-id="element-3288" />
              </div>
              <div data-id="element-3289">
                <h3 className="text-xl font-bold mb-2 text-slate-800" data-id="element-3290">
                  חודש ראשון חינם
                </h3>
                <p className="text-slate-600" data-id="element-3291">
                  לנרשמים עד{' '}
                  <strong className="text-primary" data-id="element-3292">15 בספטמבר</strong> - בניית
                  דמו של הסוכן עם הקטלוג שלכם, ללא התחייבות וללא עלות.
                </p>
              </div>
            </div>
          </div>
          <div className="mt-8 text-center" data-id="element-3293">
            <button onClick={scrollToForm} className="px-6 py-3 bg-primary text-white rounded-xl font-bold shadow-lg hover:bg-primary/90 transition-all" data-id="element-3294">
              נצלו את ההטבה - הרשמו עכשיו
            </button>
          </div>
        </div>
      </section>

      {/* LEAD FORM */}
      <section ref={formRef} className="container mx-auto px-4 py-12 max-w-3xl" data-id="element-3295">
        <div className="bg-white rounded-xl p-8 border border-gray-200 shadow-xl" data-id="element-3296">
          <h2 className="text-3xl font-bold mb-6 text-right text-slate-800" data-id="element-3297">
            התחילו <span className="text-primary" data-id="element-3298">עכשיו</span>
          </h2>
          <form onSubmit={handleSubmit} className="space-y-6" data-id="element-3299">
            <div className="text-right" data-id="element-3300">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3301">
                אימייל *
              </label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="name@brand.co.il" className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-slate-800 text-right" data-id="element-3302" />
            </div>
            <div className="text-right" data-id="element-3303">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3304">
                כתובת האתר *
              </label>
              <input type="text" name="website" value={formData.website} onChange={handleChange} required placeholder="https://yourstore.co.il" className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-slate-800 text-right" data-id="element-3305" />
            </div>
            <div className="text-right" data-id="element-3306">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3307">
                טוקן (קריאה בלבד) מקונימבו - לא חובה
              </label>
              <input type="text" name="token" value={formData.token} onChange={handleChange} placeholder="הדביקו כאן את הטוקן אם כבר קיבלתם" className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-slate-800 text-right" data-id="element-3308" />
              <p className="text-xs text-slate-500 mt-1" data-id="element-3309">
                להשגה דרך{' '}
                <a href="mailto:support@konimbo.co.il" className="text-primary underline" data-id="element-3310">
                  support@konimbo.co.il
                </a>
                . משמש לצפייה בדפי מוצר בלבד.
              </p>
            </div>
            <div className="text-right" data-id="element-3311">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3312">
                טלפון *
              </label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="050-1234567" className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-slate-800 text-right" data-id="element-3313" />
            </div>
            <div className="flex flex-wrap gap-4 justify-end pt-4" data-id="element-3314">
              <button type="submit" disabled={isSubmitting} className="px-6 py-3 bg-primary text-white rounded-xl font-bold shadow-lg hover:bg-primary/90 transition-all disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-3315">
                {isSubmitting ? <span className="flex items-center" data-id="element-3316">
                    <span className="mr-2" data-id="element-3317">שולח...</span>
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" data-id="element-3318"></span>
                  </span> : 'שליחה'}
              </button>
              <a href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ3d8hIh-SBmquhBLzT0K2NNUsyp9W7OJz3kZC5dS4y2t7kSLPqcz7Ll5kRBTrX_hUBi2XAKTHHq" target="_blank" rel="noreferrer" className="px-6 py-3 bg-gray-100 border border-gray-200 rounded-xl text-slate-700 font-bold hover:bg-gray-200 transition-all" data-id="element-3319">
                קבעו שיחה
              </a>
            </div>
          </form>
        </div>
        <p className="text-center text-sm text-slate-500 mt-6" data-id="element-3320">
          צוות Shopilots – מהוד השרון • 050‑5450460 • ben@shopilots.com
        </p>
      </section>

      {/* FOOTER CTA */}
      <section className="container mx-auto px-4 py-16 max-w-6xl text-right" data-id="element-3321">
        <div className="bg-primary/5 rounded-xl p-8 border border-primary/20 shadow-lg" data-id="element-3322">
          <h2 className="text-3xl font-bold mb-4 text-slate-800" data-id="element-3323">
            מוכנים להפוך את <span className="text-primary" data-id="element-3324">ה-AI</span> למנוע
            צמיחה?
          </h2>
          <p className="text-slate-600 mb-6 max-w-2xl mr-0 ml-auto" data-id="element-3325">
            הצטרפו לחברות המובילות שכבר משתמשות בסוכני AI כדי להגדיל מכירות,
            לשפר את חווית הלקוח ולהפחית עלויות תפעוליות.
          </p>
          <div className="flex flex-wrap gap-4 justify-end" data-id="element-3326">
            <a href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ3d8hIh-SBmquhBLzT0K2NNUsyp9W7OJz3kZC5dS4y2t7kSLPqcz7Ll5kRBTrX_hUBi2XAKTHHq" target="_blank" rel="noreferrer" className="px-6 py-3 bg-primary text-white rounded-xl font-bold shadow-lg hover:bg-primary/90 transition-all flex items-center" data-id="element-3327">
              <ChevronRight className="h-5 w-5 ml-1" data-id="element-3328" />
              דברו איתנו
            </a>
            <button onClick={scrollToForm} className="px-6 py-3 bg-gray-100 border border-gray-200 rounded-xl text-slate-700 font-bold hover:bg-gray-200 transition-all" data-id="element-3329">
              בקשו דמו
            </button>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-b from-white to-gray-50" data-id="element-3330">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3331">
          <div className="bg-primary/5 rounded-2xl p-8 md:p-12 text-center shadow-card border border-primary/20" data-id="element-3332">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-3333">
              Ready to transform your retail experience?
            </h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-3334">
              Join forward-thinking retailers using AI to boost sales and
              enhance customer experience. Start your free trial today.
            </p>
            <Button variant="primary" size="lg" onClick={() => setIsWizardOpen(true)} data-id="element-3335">
              Get Started with 1-Month Free Trial
            </Button>
          </div>
        </div>
      </section>

      {/* Add Wizard component */}
      <Wizard isOpen={isWizardOpen} onClose={() => setIsWizardOpen(false)} data-id="element-3336" />
    </div>;
}